import {
  BarChart2,
  Check,
  Inbox,
  LayoutDashboard,
  Lock,
  Palette,
  Plus,
  Users,
} from "lucide-react";
import { ConnectIDPhoneFrame } from "./ConnectIDPhoneFrame";

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard" },
  { icon: Inbox, label: "Requests", active: true },
  { icon: BarChart2, label: "Analytics" },
  { icon: Users, label: "Team" },
  { icon: Palette, label: "Branding" },
];

function StatusBadge({ pending = false }: { pending?: boolean }) {
  return pending ? (
    <span className="inline-flex items-center rounded-sm bg-[hsl(var(--brand-brass))]/20 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--brand-charcoal))]">
      Pending
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-sm bg-[hsl(var(--brand-forest))]/10 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--brand-forest))]">
      <Check className="h-2.5 w-2.5" strokeWidth={3} />
      Verified
    </span>
  );
}

function DashboardMockup() {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-md border border-[hsl(var(--brand-sand))] bg-white shadow-sm">
      <div className="flex h-10 shrink-0 items-center gap-4 border-b border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] px-4">
        <div className="flex gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
        </div>
        <div className="flex flex-1 justify-center">
          <div className="flex w-full max-w-[240px] items-center gap-2 rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-1 font-mono text-[10px] text-[hsl(var(--brand-slate))]">
            <Lock className="h-3 w-3" />
            app.fortifyid.com.au/w/sample/requests
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100%-40px)] min-h-0">
        <aside className="hidden w-[96px] shrink-0 flex-col bg-[hsl(168,25%,22%)] text-[hsl(50,25%,95%)] sm:flex">
          <div className="flex shrink-0 items-center gap-2 border-b border-[hsl(168,30%,12%)] px-3 py-3">
            <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-[hsl(42,49%,58%)]/80">
              <svg viewBox="0 0 24 24" fill="white" className="h-2.5 w-2.5">
                <path d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z" />
              </svg>
            </div>
            <span className="text-[10px] font-semibold tracking-tight">FortifyID</span>
          </div>
          <nav className="flex flex-1 flex-col gap-0.5 overflow-hidden px-1.5 pt-3">
            {NAV_ITEMS.map(({ icon: Icon, label, active }) => (
              <div
                key={label}
                className={`flex items-center gap-1.5 rounded px-2 py-1.5 text-[9px] font-medium ${
                  active
                    ? "bg-[hsl(168,32%,12%)] text-white"
                    : "text-[hsl(50,25%,80%)]"
                }`}
              >
                <Icon className="h-3 w-3 shrink-0" />
                {label}
              </div>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-1.5 border-t border-[hsl(168,30%,12%)] px-2 py-2.5">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[hsl(42,49%,58%)] text-[8px] font-semibold text-[hsl(36,4%,16%)]">
              S
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[8px] font-medium text-[hsl(50,25%,95%)]">
                Sam K.
              </span>
              <span className="truncate text-[7px] text-[hsl(168,20%,55%)]">
                Admin
              </span>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-[hsl(50,25%,95%)] p-4">
          <div className="mb-3 flex shrink-0 items-center justify-between">
            <h3 className="font-semibold text-[hsl(var(--brand-charcoal))] text-sm">
              Requests
            </h3>
            <button className="flex items-center gap-1.5 rounded bg-[hsl(var(--brand-forest))] px-2.5 py-1.5 text-xs font-medium text-[hsl(var(--brand-bone))]">
              <Plus className="h-3.5 w-3.5" />
              New request
            </button>
          </div>
          <div className="flex-1 overflow-hidden rounded border border-[hsl(var(--brand-sand))] bg-white">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--brand-sand))] bg-[hsl(50,25%,97%)] text-[hsl(var(--brand-slate))]">
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--brand-sand))] text-[hsl(var(--brand-charcoal))]">
                <tr>
                  <td className="px-3 py-2.5 font-medium">Jane Doe</td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">Bank details</td>
                  <td className="px-3 py-2.5"><StatusBadge /></td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">2m ago</td>
                </tr>
                <tr>
                  <td className="px-3 py-2.5 font-medium">Atlas Industries</td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">Bank details</td>
                  <td className="px-3 py-2.5"><StatusBadge /></td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">5m ago</td>
                </tr>
                <tr>
                  <td className="px-3 py-2.5 font-medium">John Smith</td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">Contact info</td>
                  <td className="px-3 py-2.5"><StatusBadge pending /></td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">1h ago</td>
                </tr>
                <tr>
                  <td className="px-3 py-2.5 font-medium">Global Trading</td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">Bank details</td>
                  <td className="px-3 py-2.5"><StatusBadge /></td>
                  <td className="px-3 py-2.5 text-[hsl(var(--brand-slate))]">3h ago</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HeroProductMockup() {
  return (
    <div
      className="relative min-h-[390px] w-full sm:min-h-[460px] md:min-h-[500px]"
      data-testid="hero-product-mockup"
    >
      <DashboardMockup />
      <div className="absolute bottom-[-42px] right-[-4px] z-20 w-[158px] sm:bottom-[-64px] sm:right-[-10px] sm:w-[190px] lg:w-[200px]">
        <ConnectIDPhoneFrame
          className="w-full"
          variant="simple"
          data-testid="hero-connectid-phone-frame"
        />
      </div>
    </div>
  );
}